import React, { useEffect, useRef, useState } from "react";
import { Shield, Volume2, VolumeX, ArrowRight, AlertTriangle } from "lucide-react";
import {
    speakSequence,
    cancel as cancelSpeech,
    isMuted,
    setMuted,
} from "../lib/speech";
import { useI18n } from "../lib/i18n";

export const SafetyBriefing = ({ mode, onNext, onSkipToCpr }) => {
    const { t, lang } = useI18n();
    const title = t("safety_title");
    const lines = [
        t("safety_line_1"),
        t("safety_line_2"),
        t("safety_line_3"),
        t("safety_line_4"),
    ];
    const spoken = mode === "demo"
        ? [`${t("demo_tts_prefix")} ${title}`, ...lines]
        : [title, ...lines];

    const [activeLine, setActiveLine] = useState(-1);
    const [muted, setMutedState] = useState(isMuted());
    const cancelRef = useRef(() => {});

    // Re-run speech when language changes too (fresh cache key, fresh audio).
    useEffect(() => {
        cancelRef.current = speakSequence(spoken, {
            onLineStart: (i) => setActiveLine(i - 1),
            onDone: () => {
                setActiveLine(lines.length);
                onNext?.();
            },
        });
        return () => {
            cancelRef.current?.();
            cancelSpeech();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        setMutedState(next);
        if (next) cancelRef.current?.();
    };

    return (
        <section
            data-testid="safety-briefing-section"
            className="bg-white border-2 border-zinc-200 rounded-3xl p-6 sm:p-10 shadow-sm anim-fade-up"
        >
            {mode === "demo" && onSkipToCpr && (
                <button
                    data-testid="safety-skip-to-cpr-link"
                    onClick={() => {
                        cancelRef.current?.();
                        cancelSpeech();
                        onSkipToCpr();
                    }}
                    className="mb-5 w-full rounded-2xl border-2 border-[#245288]/20 bg-white hover:bg-[#245288] hover:border-[#245288] hover:text-white text-[#245288] active:scale-[0.99] text-left px-5 py-4 flex items-center justify-between gap-3 transition-all"
                >
                    <span className="min-w-0">
                        <span className="block font-display font-black text-base sm:text-lg leading-tight">
                            {t("landing_skip_to_cpr")}
                        </span>
                        <span className="block text-xs sm:text-sm opacity-80 leading-tight mt-0.5">
                            {t("landing_skip_to_cpr_sub")}
                        </span>
                    </span>
                    <ArrowRight className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
                </button>
            )}

            <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#A72920]/10 text-[#A72920] flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5" />
                    </div>
                    <h2
                        className={`font-display font-black text-2xl sm:text-4xl tracking-tight leading-tight transition-colors ${
                            activeLine === -1 ? "text-[#A72920]" : "text-zinc-900"
                        }`}
                    >
                        {title}
                    </h2>
                </div>
                <button
                    data-testid="tts-mute-toggle"
                    onClick={toggleMute}
                    aria-label={muted ? t("tts_activate") : t("tts_mute")}
                    className="flex-shrink-0 w-11 h-11 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition"
                >
                    {muted ? (
                        <VolumeX className="w-5 h-5 text-zinc-600" />
                    ) : (
                        <Volume2 className="w-5 h-5 text-zinc-900" />
                    )}
                </button>
            </div>

            <ol
                data-testid="safety-briefing-list"
                className="space-y-3 sm:space-y-4"
            >
                {lines.map((line, i) => {
                    const isActive = activeLine === i;
                    const isPast = activeLine > i;
                    return (
                        <li
                            key={i}
                            data-testid={`safety-line-${i}`}
                            className={`flex items-start gap-4 rounded-2xl border-2 p-4 sm:p-5 transition-all ${
                                isActive
                                    ? "border-[#A72920] bg-[#A72920]/5 scale-[1.01]"
                                    : isPast
                                      ? "border-zinc-200 bg-zinc-50 opacity-70"
                                      : "border-zinc-200 bg-white"
                            }`}
                        >
                            <div
                                className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-display font-black text-lg sm:text-xl ${
                                    isActive
                                        ? "bg-[#A72920] text-white"
                                        : "bg-zinc-900 text-white"
                                }`}
                            >
                                {i + 1}
                            </div>
                            <div className="pt-1 sm:pt-2 font-display font-bold text-lg sm:text-2xl tracking-tight leading-tight">
                                {line}
                            </div>
                        </li>
                    );
                })}
            </ol>

            <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
                <AlertTriangle className="w-3.5 h-3.5" />
                {t("safety_hint")}
            </div>

            <button
                data-testid="safety-continue-button"
                onClick={() => {
                    cancelRef.current?.();
                    cancelSpeech();
                    onNext();
                }}
                className="group mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#245288] text-white font-display font-black text-base sm:text-lg px-6 py-4 rounded-2xl hover:bg-[#A72920] active:scale-[0.99] transition-all"
            >
                {t("safety_continue")}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
            </button>
        </section>
    );
};
